import { Component } from '@theme/component';

/**
 * Benefits carousel.
 *
 * The authored cards live in a single group; the group is duplicated at runtime so the `-50%`
 * keyframe loops seamlessly. Exactly one duplicate is made — the loop duration comes from
 * "seconds per benefit × number of benefits", so extra copies would multiply the perceived speed.
 *
 * The animation is paused on hover (CSS), and suspended while the section is off-screen or the tab
 * is hidden. With auto-scroll off, or under `prefers-reduced-motion`, the row degrades to a
 * manually scrollable strip and the duplicate group is hidden.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} track
 * @property {HTMLElement} group
 * @property {HTMLElement[]} [cards]
 *
 * @extends {Component<Refs>}
 */
export class OcBenefitsCarouselComponent extends Component {
  requiredRefs = ['viewport', 'track', 'group'];

  /** Left inset kept between the viewport edge and the revealed card, in pixels. */
  static REVEAL_INSET = 16;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #inViewport = true;

  /** Index of the benefit selected in the theme editor, or -1. */
  #selected = -1;

  connectedCallback() {
    super.connectedCallback();

    this.#duplicate();
    this.#listen();
    this.#observe();
    this.#listenToEditor();
    this.#updatePlayState();
  }

  /** Re-runs after a Section Rendering API re-render + morph, which drops the duplicate. */
  updatedCallback() {
    super.updatedCallback();

    this.#duplicate();
    this.#updatePlayState();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;

    this.#observer?.disconnect();
    this.#observer = null;

    super.disconnectedCallback();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /** The authored cards, in document order. */
  get cards() {
    const { group } = this.refs;

    return group ? /** @type {HTMLElement[]} */ (Array.from(group.querySelectorAll('[data-oc-benefits-card]'))) : [];
  }

  /* -------------------------------------------------------------------------- */
  /* Public API — the shape `assets/theme-editor.js` calls on a slideshow.       */
  /* -------------------------------------------------------------------------- */

  /** Stops the scrolling animation. */
  pause() {
    this.paused = true;
  }

  /** Restarts the scrolling animation and lets the track fall back to its keyframes. */
  resume() {
    this.#selected = -1;
    this.#release();
    this.paused = false;
  }

  /**
   * Holds the carousel still and brings the selected benefit into view (HORIZON-SPEC 10.2).
   *
   * @param {number|string} index - The index of the benefit to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to scroll smoothly to the benefit.
   */
  select(index, event, { animate = true } = {}) {
    const target = Number(index);

    this.#selected = Number.isFinite(target) ? target : -1;
    this.pause();

    const card = this.cards[this.#selected];

    if (card) this.#reveal(card, animate);
  }

  /* -------------------------------------------------------------------------- */
  /* Layout                                                                     */
  /* -------------------------------------------------------------------------- */

  /** Makes the track hold exactly two copies of the authored group. Safe to call repeatedly. */
  #duplicate() {
    const { track, group } = this.refs;

    if (!track || !group || !group.isConnected) return;

    for (const child of Array.from(track.children)) {
      if (child !== group) child.remove();
    }

    track.appendChild(this.#cloneGroup(group));
  }

  /**
   * @param {Element} node
   * @returns {Element}
   */
  #cloneGroup(node) {
    const clone = /** @type {Element} */ (node.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');

    for (const element of [clone, ...Array.from(clone.querySelectorAll('*'))]) {
      element.removeAttribute('ref');
      element.removeAttribute('id');
      element.removeAttribute('data-oc-benefits-card');

      for (const attribute of Array.from(element.attributes)) {
        if (attribute.name.startsWith('data-shopify-editor')) element.removeAttribute(attribute.name);
      }
    }

    for (const image of Array.from(clone.querySelectorAll('img'))) {
      image.setAttribute('loading', 'lazy');
      image.removeAttribute('fetchpriority');
    }

    return clone;
  }

  /** The X translation the running keyframes currently apply to the track. */
  #currentTranslate() {
    const { track } = this.refs;

    if (!track) return 0;

    try {
      const value = getComputedStyle(track).transform;

      if (!value || value === 'none') return 0;

      const parts = value.match(/matrix.*\((.+)\)/);
      if (!parts?.[1]) return 0;

      const numbers = parts[1].split(',').map((part) => parseFloat(part));

      return (numbers.length > 6 ? numbers[12] : numbers[4]) ?? 0;
    } catch {
      return 0;
    }
  }

  /**
   * Brings a card into view: by scrolling when the viewport scrolls (static / reduced motion),
   * otherwise by freezing the track and translating it into place.
   *
   * @param {HTMLElement} card
   * @param {boolean} animate
   */
  #reveal(card, animate) {
    const { viewport, track } = this.refs;

    if (!viewport || !track) return;

    const inset = OcBenefitsCarouselComponent.REVEAL_INSET;
    const scrollable =
      viewport.scrollWidth > viewport.clientWidth + 1 && getComputedStyle(viewport).overflowX !== 'hidden';

    if (scrollable) {
      viewport.scrollTo({ left: Math.max(0, card.offsetLeft - inset), behavior: animate ? 'smooth' : 'auto' });
      return;
    }

    const startX = this.#currentTranslate();
    const delta = card.getBoundingClientRect().left - viewport.getBoundingClientRect().left - inset;

    track.classList.add('is-block-focused');
    track.style.transform = `translateX(${startX - delta}px)`;
  }

  /** Hands the track back to its keyframes. */
  #release() {
    const { track } = this.refs;

    if (!track) return;

    track.classList.remove('is-block-focused');
    track.style.transform = '';
  }

  /* -------------------------------------------------------------------------- */
  /* Events                                                                     */
  /* -------------------------------------------------------------------------- */

  #listen() {
    this.#abort?.abort();
    this.#abort = new AbortController();

    const { signal } = this.#abort;

    document.addEventListener('visibilitychange', () => this.#updatePlayState(), { signal });
  }

  #observe() {
    if (!('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#inViewport = entry.isIntersecting;

        this.#updatePlayState();
      },
      { rootMargin: '120px 0px' }
    );

    this.#observer.observe(this);
  }

  /**
   * `assets/theme-editor.js` only knows about `slideshow-component`, and it is overwritten on
   * every theme update — so this component carries its own listener (HORIZON-SPEC 10.2).
   */
  #listenToEditor() {
    if (!window.Shopify?.designMode) return;

    const signal = this.#abort?.signal;

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!(event.target instanceof HTMLElement)) return;

        const card = event.target.closest('[data-oc-benefits-card]');
        if (!card || card.closest('oc-benefits-carousel-component') !== this) return;

        this.select(card.getAttribute('data-index') ?? -1, event);
      },
      { signal }
    );

    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (!(event.target instanceof HTMLElement)) return;
        if (event.target.closest('oc-benefits-carousel-component') !== this) return;

        this.resume();
      },
      { signal }
    );
  }

  /** Suspends the animation off-screen and in a hidden tab, without touching the public paused state. */
  #updatePlayState() {
    const suspended = document.hidden || !this.#inViewport;

    if (suspended) {
      this.setAttribute('data-suspended', '');
    } else {
      this.removeAttribute('data-suspended');
    }
  }
}

if (!customElements.get('oc-benefits-carousel-component')) {
  customElements.define('oc-benefits-carousel-component', OcBenefitsCarouselComponent);
}
